/**
 * The learner's own pathway certificates — the authenticated half of the feature.
 *
 * Separate from `certificate-view.ts`, which fetches ONE certificate by its public id with
 * no credentials, for a visitor who is usually not the person who earned it. Everything
 * here answers "what do *I* hold", so every call is signed in.
 *
 *   GET /api/pathways/v1/certificates             my pathway certificates, this academy
 *   GET /api/pathways/v1/paths/<key>/certificate  do I hold one for this pathway
 *
 * THE STATE ON THE PATHWAY PAGE COMES FROM THE SERVER, NOT FROM `paths.ts`.
 * `paths.ts` computes pathway completion in the browser from per-course progress, and that
 * computation returns ZERO on any error (`catalogue.ts::myCourseProgress`, "NEVER THROWS").
 * It is the right source for a progress bar and the wrong one for a credential: a network
 * blip would tell a learner they had not finished. So the certificate section asks the
 * server, which decides from durable `GeneratedCertificate` rows it wrote itself.
 *
 * THE SECOND ENDPOINT ALSO ISSUES. It runs the same idempotent award check the completion
 * receiver runs, before answering — the lazy top-up. That is what covers a learner who
 * finished while this was half-deployed, or whose completion signal was missed. The browser
 * supplies no facts and could not: it asks a question and the server answers it.
 */

import { openedx } from './openedx';
import type { IssuedPathwayCertificate } from './certificate-view';

/** The learner's own view of one of their pathway certificates. */
export interface MyPathwayCertificate extends IssuedPathwayCertificate {
  /** Present only on the learner's own rows — it is their own pathway. */
  pathway_key: string;
}

interface MyListResponse {
  api_version: string;
  count: number;
  results: MyPathwayCertificate[];
}

interface ForPathResponse {
  api_version: string;
  /** "issued" | "already_issued" | "incomplete" | "revoked" | "skipped". Diagnostic only. */
  outcome: string;
  /** Null when this learner has not earned one. NOT an error — see the backend view. */
  certificate: MyPathwayCertificate | null;
}

export async function listMyPathwayCertificates(): Promise<MyPathwayCertificate[]> {
  const response = await openedx.get<MyListResponse>('/api/pathways/v1/certificates');
  return response.results ?? [];
}

/**
 * This learner's certificate for one pathway, or null.
 *
 * NULL IS AN ANSWER, NOT A FAILURE. The endpoint returns 200 with `certificate: null` for
 * a learner who has not finished, because "you have not earned one" and "there is no such
 * pathway" are different facts the page renders very differently — collapsing them into a
 * 404 is how an in-progress pathway comes to look like a broken link.
 */
export async function getMyPathwayCertificate(
  pathKey: string,
): Promise<MyPathwayCertificate | null> {
  const response = await openedx.get<ForPathResponse>(
    `/api/pathways/v1/paths/${encodeURIComponent(pathKey)}/certificate`,
  );
  return response.certificate ?? null;
}

/** Where the document lives. A real route in this app, not a proxied platform page. */
export const pathwayCertificateHref = (uuid: string) => `/certificates/path/${uuid}`;
